// EAIRI — the typed client for the edge functions.
//
// One place where the front end talks to the server, so the contract is stated
// once. Two rules the handoff broke and this file makes structurally hard:
//
//   * NO AMOUNT IS EVER SENT. A checkout names a SKU; the server prices it.
//     There is no parameter here that could carry a price.
//   * A REFUSAL KEEPS ITS i18n KEY. Every domain error arrives with a stable
//     code and a key, so the surface renders French without a switch statement
//     mapping English server strings back to French.

export type Lang = 'fr' | 'en';

export interface AccountEntitlement {
  id: string; kind: string; state: 'tostart' | 'inprog' | 'ready' | 'revoked';
  fn: string | null; ref: string | null; created_at: string;
}
export interface AccountRun {
  id: string; ref: string; instrument: string;
  status: 'open' | 'closed' | 'abandoned';
  assessor: string; closed_at: string | null; created_at: string;
}
export type DeliveryState = 'ready' | 'withdrawn' | 'rendering' | 'failed' | 'pending';

/** A delivered file's kind — the render queue's names. */
export type ArtifactFormat = 'report' | 'pdf' | 'pptx' | 'model';

export interface AccountDocument {
  id: string; kind: string; run_ref: string; lang: string;
  sha256: string | null; created_at: string;
  /** Decided server-side by domain/delivery.ts, never inferred here. */
  state: DeliveryState;
  /** What the delivering job uploaded, in order; absent until delivered. */
  artifacts?: ArtifactFormat[];
  /** While composing a multi-file document: files in storage over files owed. */
  progress?: { done: number; owed: number } | null;
}

/** A signed link, and what it is a link to. Deliberately short-lived: once
    minted the URL IS the access, with no further check behind it. */
export interface DownloadGrant {
  url: string; filename: string; expiresInSeconds: number;
  digest: string | null; ref: string;
  /** Which delivered file the link is for. */
  artifact?: ArtifactFormat;
}
export interface AccountState {
  provisioned: boolean;
  org: { id: string; name: string; sector: string | null; size_band: string | null;
         country: string | null; lang: string; logo_path?: string | null;
         /** A short-lived signed URL to the organisation's mark, when it has one. */
         logoUrl?: string | null } | null;
  entitlements?: AccountEntitlement[];
  runs?: AccountRun[];
  documents?: AccountDocument[];
  renderJobs?: { id: string; sku: string | null; kind: string; lang: string;
                 run_ref: string; status: string; requested_at: string }[];
  calibration?: { id: string; ref: string; status: string } | null;
  composite?: { orgReady: boolean; k: number; closedRuns: number; functions: string[]; eligible: boolean };
  trajectory?: { available: boolean; currentRef: string | null; previousRef: string | null };
}

/** The shape every edge function returns on a refusal. */
/** What a public verification returns. Deliberately narrow: there is no field
    here that could carry the organisation, a score or a download. */
export type VerificationResult =
  | { found: false; digest: string; message: string }
  | {
    found: true; digest: string; kind: string; kindLabel: string; lang: string;
    reference: string; composedAt: string; rendered: boolean; message: string;
    versions: { engine: string; registry: string; bank: string };
  };

/* ------------------------------------------------ the falsification loop -- */

export type ProofState = 'due' | 'overdue' | 'provided' | 'revised';

export interface ProofView {
  id: string; qid: string; ref: string; level: number; owner: string;
  dueAt: string; state: ProofState; daysLeft: number | null;
  evidence: string | null; decidedAt: string | null; decidable: boolean;
}

export interface RegisterSummary {
  total: number; due: number; overdue: number; provided: number; revised: number;
  /** Null until something is decided — a rate over zero decisions is not a rate. */
  substantiationRate: number | null;
  nextDueAt: string | null;
}

export interface PlEntry { pl: string; band: string; dri?: string }

export interface PlTestView {
  ref: string; declaredAt: string; dueAt: string;
  daysLeft: number; overdue: boolean; entries: PlEntry[];
}

export interface PlVocabulary {
  lines: { code: string; label: string }[];
  bands: { code: string; label: string }[];
}

export interface Delta { code: string; previous: number; current: number; delta: number }

export type TrajectoryResult =
  | { available: false; reason: 'needs_two_runs'; closedOrgRuns: number }
  | { available: false; reason: 'instrument_changed'; previousRef: string;
      currentRef: string; changed: string[] }
  | {
      available: true; previousRef: string; currentRef: string;
      previousClosedAt: string; currentClosedAt: string; daysBetween: number;
      owr: Delta; ocr: Delta; sii: Delta; ceiling: Delta;
      domains: (Delta & { name: string })[];
      constraintMoved: boolean;
      previousConstraint: string; currentConstraint: string;
      previousConstraintName: string; currentConstraintName: string;
      maturityChanged: boolean;
      versions: { engine: string; registry: string; bank: string };
    };

/* ------------------------------------------------------------------ ops -- */

export interface OpsSnapshot {
  health: { metric: string; count: number; detail: unknown }[];
  renderJobs: {
    id: string; org_id: string; kind: string; sku: string | null; format: string | null;
    lang: string; run_ref: string; status: string; attempts: number;
    dead_letter: boolean; fail_reason: string | null;
    requested_at: string; lease_until: string | null;
  }[];
  outbox: {
    id: string; org_id: string | null; kind: string; state: string;
    attempts: number; error: string | null; scheduled_for: string; created_at: string;
  }[];
  bookings: {
    id: string; org_id: string; run_ref: string; status: string;
    requested_by: string; contact_email: string; created_at: string;
  }[];
  contacts: {
    id: string; subject: string; name: string; email: string;
    organisation: string | null; message: string; lang: string;
    state: string; created_at: string; answered_at: string | null; note: string | null;
  }[];
  now: string;
}

export interface ApiErrorBody {
  error: string;
  i18nKey: string;
  details?: Record<string, unknown>;
  correlationId?: string;
}

/** A refusal, carrying everything the surface needs to explain it. */
export class ApiError extends Error {
  readonly code: string;
  readonly i18nKey: string;
  readonly status: number;
  readonly details: Record<string, unknown>;
  readonly correlationId: string | null;

  constructor(status: number, body: ApiErrorBody) {
    super(body.error);
    this.name = "ApiError";
    this.status = status;
    this.code = body.error;
    this.i18nKey = body.i18nKey;
    this.details = body.details ?? {};
    this.correlationId = body.correlationId ?? null;
  }

  /** True when retrying the same request could plausibly succeed. A refusal on
   *  a business rule will refuse again; a network blip will not.
   *
   *  The status alone is not enough to tell those apart. `invariant_violation`
   *  is mapped to 500 because it is also what a genuine internal inconsistency
   *  throws, but it is what EVERY unmet precondition throws: no bearer token,
   *  not a member of this organisation, not a uuid, missing field, malformed
   *  body, expired link. None of those become true on the second attempt, and
   *  retrying them costs the person two backoffs before the surface tells them
   *  anything. An unexpected fault carries `internal_error`, not a domain code,
   *  and is still retried. */
  get retryable(): boolean {
    if (this.code === "invariant_violation") return false;
    return this.status >= 500 || this.code === "network";
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

/** Build the error the surface will render when the network itself fails.
    Given its own key so the message can say "your earlier answers are saved",
    which is true and is the thing a user actually wants to know. */
function networkError(): ApiError {
  return new ApiError(0, { error: "network", i18nKey: "eairi.errors.network" });
}

export interface ClientConfig {
  readonly baseUrl: string;
  /** Returns the current access token, or null when signed out. */
  readonly getToken: () => string | null | Promise<string | null>;
  readonly fetchImpl?: typeof fetch;
  /** Total attempts for a retryable failure, including the first. */
  readonly maxAttempts?: number;
  /** Injected so tests do not actually wait. */
  readonly sleep?: (ms: number) => Promise<void>;
}

export interface RequestOptions {
  /** A capability token instead of a session — calibration participants. */
  readonly capabilityToken?: string;
  readonly signal?: AbortSignal;
  /** Opt out of retrying, for a call that must not be repeated. */
  readonly noRetry?: boolean;
}

const defaultSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export class EairiClient {
  private readonly cfg: Required<Omit<ClientConfig, "fetchImpl">> & { fetchImpl: typeof fetch };

  constructor(cfg: ClientConfig) {
    this.cfg = {
      baseUrl: cfg.baseUrl.replace(/\/+$/, ""),
      getToken: cfg.getToken,
      fetchImpl: cfg.fetchImpl ?? globalThis.fetch.bind(globalThis),
      maxAttempts: cfg.maxAttempts ?? 3,
      sleep: cfg.sleep ?? defaultSleep,
    };
  }

  private async call<T>(fn: string, body: unknown, opts: RequestOptions = {}): Promise<T> {
    const attempts = opts.noRetry ? 1 : this.cfg.maxAttempts;
    let last: ApiError | null = null;

    for (let attempt = 1; attempt <= attempts; attempt++) {
      const token = await this.cfg.getToken();
      const headers: Record<string, string> = { "content-type": "application/json" };
      if (token) headers.authorization = `Bearer ${token}`;
      if (opts.capabilityToken) headers["x-eairi-token"] = opts.capabilityToken;

      let res: Response;
      try {
        res = await this.cfg.fetchImpl(`${this.cfg.baseUrl}/${fn}`, {
          method: "POST",
          headers,
          body: JSON.stringify(body ?? {}),
          ...(opts.signal ? { signal: opts.signal } : {}),
        });
      } catch {
        last = networkError();
        if (attempt < attempts) { await this.cfg.sleep(backoff(attempt)); continue; }
        throw last;
      }

      if (res.ok) return (await res.json()) as T;

      let parsed: ApiErrorBody;
      try {
        parsed = (await res.json()) as ApiErrorBody;
      } catch {
        // A response we cannot parse is still a failure; do not surface the raw
        // body, which may not be ours.
        parsed = { error: "internal_error", i18nKey: "eairi.errors.internal_error" };
      }
      // A 401 from the platform gateway — a rejected or expired JWT, refused
      // before any handler runs — carries a body that is not ours and must not
      // reach a person. A 401 the application itself raised carries a domain
      // code and a reason worth keeping. The i18n key is what distinguishes
      // them: it is ours by construction, and a gateway cannot produce one.
      const isOurs = typeof parsed.i18nKey === "string"
        && parsed.i18nKey.startsWith("eairi.errors.");
      if (res.status === 401 && !isOurs) {
        throw new ApiError(401, { error: "unauthenticated", i18nKey: "eairi.errors.unauthenticated" });
      }
      last = new ApiError(res.status, parsed);
      // A business refusal will refuse again. Only server faults are retried.
      if (!last.retryable || attempt === attempts) throw last;
      await this.cfg.sleep(backoff(attempt));
    }
    throw last ?? networkError();
  }

  /* ------------------------------------------------------------ runs ---- */

  startRun(input: {
    orgId: string; entitlementId: string; assessor: string; fn?: string;
    context?: Record<string, unknown>;
  }): Promise<{ runId: string; ref: string; instrument: string; engineVersion: string; bankVersion: string }> {
    // Starting consumes an entitlement. Never retried automatically: the server
    // is safe against a double-click, but a silent client retry would still be
    // a second deliberate attempt at spending something.
    return this.call("eairi-run-start", input, { noRetry: true });
  }

  answer(input: { orgId: string; runId: string; qid: string; level: 1 | 2 | 3 | 4 | 5 }):
    Promise<{ runId: string; qid: string; level: number; answered: number }> {
    // Safe to retry: writing the same answer twice is the same answer.
    return this.call("eairi-run-answer", input);
  }

  submitRun(input: { orgId: string; runId: string }): Promise<{
    runId: string; ref: string; status: "closed"; result: unknown; proofsRaised: number;
    reproducibility: { digest: string; stamp: string };
  }> {
    return this.call("eairi-run-submit", input, { noRetry: true });
  }

  /** Resume a paid run, or read its result once closed. */
  getRun(runId: string, lang: Lang): Promise<{
    run: { id: string; ref: string; instrument: string; status: 'open' | 'closed' | 'abandoned';
           assessor: string; closedAt: string | null };
    instrument?: { code: string; lang: string; bankVersion: string;
      questions: { qid: string; position: number; domainCode: string; title: string; text: string;
        anchors: { level: number; label: string; text: string }[] }[] };
    answers: Record<string, number>;
    view?: unknown;
  }> {
    return this.call('eairi-run-get', { runId, lang });
  }

  /* --------------------------------------------------------- account ---- */

  /** Provision the caller's organisation on first sign-in. Idempotent. */
  /** First visit: provisions the organisation. Later: edits its profile —
      the name, sector, size band and country every document's cover prints. */
  accountSetup(input: { name: string; lang: Lang; sector?: string; size?: string; country?: string }):
    Promise<{ orgId: string; userId: string; updated?: boolean }> {
    return this.call('eairi-account-setup', input, { noRetry: true });
  }

  /** The organisation's mark for its document covers: a PNG or JPEG (400 KB,
      1600 px) as base64, or `remove`. Owner only. */
  orgLogo(input: { orgId: string; data?: string; remove?: boolean }):
    Promise<{ orgId: string; logo: { path: string; width: number; height: number; kind: string; bytes: number } | null }> {
    return this.call('eairi-org-logo', input, { noRetry: true });
  }

  /** Everything the hub shows, in one round trip. Scoped by RLS, not by us. */
  account(): Promise<AccountState> {
    return this.call('eairi-account', {});
  }

  /** The cross-layer read. Refuses with composite_gate_unmet and its shortfall. */
  composite(orgId: string, lang: Lang): Promise<unknown> {
    return this.call('eairi-composite', { orgId, lang });
  }

  /** The contact desk. Answers the same way whether the request was filed,
      throttled or trapped — the caller is told only that it was received. */
  contact(input: {
    subject: string; name: string; email: string; organisation: string | null;
    message: string; website: string; lang: Lang;
  }): Promise<{ received: true }> {
    return this.call("eairi-contact", input, { noRetry: true });
  }

  /** Turn a delivered readout into a scoped working session. */
  requestBooking(input: {
    orgId: string; runRef: string; requestedBy: string; contactEmail: string;
    constraintDomain?: string; headline?: string; notes?: string;
  }): Promise<{ bookingId: string; status: string }> {
    return this.call('eairi-booking', input, { noRetry: true });
  }

  /* ------------------------------------------------ team calibration ---- */

  teamStart(input: { orgId: string; entitlementId: string; ownerName: string; ownerEmail: string;
    context?: Record<string, unknown> }): Promise<{ calibrationId: string; ref: string; ownerToken: string }> {
    return this.call('eairi-team-start', input, { noRetry: true });
  }

  /** Adds an assessor and emails them their personal link. `delivered` says
      whether the mail went; the link is returned either way so the owner can
      forward it by hand when it did not. */
  teamInvite(input: { orgId: string; calibrationId: string; email: string; who?: string; role?: string }):
    Promise<{ link: string; delivered?: boolean; mailerConfigured?: boolean; participantId?: string }> {
    return this.call('eairi-team-invite', input, { noRetry: true });
  }

  /** Counts and a roster. Never answers — the endpoint does not return them. */
  teamStatus(orgId: string): Promise<unknown> {
    return this.call('eairi-team-status', { orgId });
  }

  teamClose(input: { orgId: string; calibrationId: string; lang: Lang }): Promise<unknown> {
    return this.call('eairi-team-close', input, { noRetry: true });
  }

  /** The invited assessor's one endpoint: read, answer, finish. */
  participate(token: string, input: Record<string, unknown>): Promise<unknown> {
    return this.call('eairi-participate', input,
      { capabilityToken: token, noRetry: input.action !== 'answer' });
  }

  /* ------------------------------------------------- free diagnostic ---- */

  /** Anonymous. Returns the capability token once, plus the instrument in the
      requested language — the bank itself stays on the server. */
  freeStart(input: { lang: Lang; context?: Record<string, unknown> }): Promise<{
    sessionToken: string;
    instrument: {
      code: string; lang: string; bankVersion: string;
      questions: { qid: string; position: number; domainCode: string; title: string; text: string;
        anchors: { level: number; label: string; text: string }[] }[];
    };
  }> {
    return this.call("eairi-free-start", input, { noRetry: true });
  }

  freeAnswer(token: string, input: { qid: string; level: 1 | 2 | 3 | 4 | 5 }):
    Promise<{ qid: string; level: number; answered: number }> {
    return this.call("eairi-free-answer", input, { capabilityToken: token });
  }

  /** Returns the FREE VIEW: score, constraint, maturity, panel. The full result
      exists on the server and is deliberately not sent. */
  freeSubmit(token: string): Promise<{
    tier: "free"; owr: number; ocr: number; sii: number; siiBand: number;
    maturityWeighted: string; maturityConstrained: string;
    constraint: { code: string; name: string };
    panel: { label: string; consensusMeasured: boolean };
    n: number; withheld: string[];
  }> {
    return this.call("eairi-free-submit", {}, { capabilityToken: token, noRetry: true });
  }

  /** The public catalog. Every price the interface shows comes from here. */
  catalog(lang: Lang): Promise<{
    lang: string; taxBehaviour: 'exclusive'; currency: 'eur';
    reports: { code: string; kind: string; name: string; desc: string;
      amountCents: number; price: string; featured: boolean; grants: string[] }[];
    decks: { code: string; kind: string; name: string; desc: string;
      amountCents: number; price: string; featured: boolean; grants: string[] }[];
  }> {
    return this.call('eairi-catalog', { lang });
  }

  /** Attach an address to a submitted free diagnostic and send the
      confirmation link. The address joins nothing until the link is followed. */
  freeOptinRequest(sessionToken: string, input: { email: string; lang: Lang }):
    Promise<{ requested: boolean; mailerConfigured: boolean }> {
    return this.call('eairi-free-optin', { action: 'request', ...input },
      { capabilityToken: sessionToken, noRetry: true });
  }

  /** Consume a confirmation link. Works exactly once. */
  freeOptinConfirm(token: string): Promise<{ confirmed: boolean }> {
    return this.call('eairi-free-optin', { action: 'confirm', token }, { noRetry: true });
  }

  /** Confirm that a document was composed by Gradixia and not altered since.
   *
   *  Public and unauthenticated on purpose: an attestation only a customer can
   *  check is not an attestation. It answers about the artefact — never the
   *  organisation, never a score, never a link. */
  verify(digest: string, lang: Lang): Promise<VerificationResult> {
    return this.call('eairi-verify', { digest, lang }, { noRetry: true });
  }

  /** Mint a short-lived link to a delivered document.
   *
   *  Never retried: each call is audited as a download of a confidential board
   *  document, and a retry would record a fetch that never happened. */
  download(input: { orgId: string; documentId: string; artifact?: ArtifactFormat }): Promise<DownloadGrant> {
    return this.call('eairi-download', input, { noRetry: true });
  }

  /* --------------------------------------------- falsification loop ---- */

  /** Every level declared 4 or 5, and what became of it. */
  proofs(orgId: string): Promise<{ proofs: ProofView[]; summary: RegisterSummary; now: string }> {
    return this.call('eairi-proofs', { orgId, action: 'list' });
  }

  /** Record that a claim was evidenced, or revised down.
   *
   *  Never retried: it is decided once, and a retry after a lost response would
   *  report a second decision that the database has already refused. */
  decideProof(input: {
    orgId: string; proofId: string; status: 'fournie' | 'revisee'; evidence: string;
  }): Promise<{ decided: ProofView }> {
    return this.call('eairi-proofs', { action: 'decide', ...input }, { noRetry: true });
  }

  /** The D+90 result test, and whether the composite gate allows declaring it. */
  plTest(orgId: string, lang: Lang): Promise<{
    declared: PlTestView | null; eligible: boolean;
    shortfall: Record<string, unknown> | null; vocabulary: PlVocabulary; now: string;
  }> {
    return this.call('eairi-pl-test', { orgId, lang, action: 'get' });
  }

  /** Declare the three programmes. Never retried — it starts a ninety-day clock. */
  declarePlTest(input: { orgId: string; lang: Lang; entries: PlEntry[] }): Promise<{
    declared: PlTestView; vocabulary: PlVocabulary;
  }> {
    return this.call('eairi-pl-test', { action: 'declare', ...input }, { noRetry: true });
  }

  /** Two closed organisational runs compared — or why they cannot be. */
  trajectory(orgId: string, lang: Lang): Promise<TrajectoryResult> {
    return this.call('eairi-trajectory', { orgId, lang });
  }

  /* ------------------------------------------------------------- ops ---- */

  /** The operator console. Refuses anyone without the operator claim — the
      database decides, not this client. */
  opsHealth(): Promise<OpsSnapshot> {
    return this.call('eairi-ops', { action: 'health' });
  }

  /** Answer or close a contact request. The database decides who may: the
      function asks eairi_is_ops() itself and refuses a return to `new`. */
  resolveContact(id: string, state: "answered" | "closed", note?: string):
    Promise<{ resolved: string; state: string }> {
    return this.call("eairi-ops", { action: "resolve", id, state, ...(note ? { note } : {}) },
      { noRetry: true });
  }

  /** Put a dead-lettered render back on the queue, once its cause is fixed. */
  requeueRender(jobId: string, note?: string): Promise<{ requeued: unknown }> {
    return this.call('eairi-ops', { action: 'requeue', jobId, ...(note ? { note } : {}) },
      { noRetry: true });
  }

  /* -------------------------------------------------------- commerce ---- */

  /** Names a SKU. There is deliberately no amount parameter — the catalog on
      the server is the only thing that knows what anything costs. */
  checkout(input: { orgId: string; sku: string; lang: Lang }): Promise<{
    sessionId: string; url: string; sku: string; subtotalCents: number;
  }> {
    return this.call("eairi-checkout", input, { noRetry: true });
  }
}

/** Exponential backoff with a small deterministic jitter step, so a fleet of
    clients recovering together does not retry in lockstep. */
export function backoff(attempt: number): number {
  const base = Math.min(8000, 250 * 2 ** (attempt - 1));
  return base + (attempt * 37) % 250;
}
